<!--
  AppBar — the one top bar shared by every section of the unified SPA
  (site / remote / admin). Carries:

    • The Optio wordmark on the left, with a wayfinding accent dot and an
      optional section breadcrumb (e.g. "Optio › Remote") that mirrors
      `router.section` so the user always knows which surface they're on.
      Clicking the brand returns to /.

    • An optional `leading` snippet for a per-shell button at the very
      start of the bar (admin uses it for the mobile drawer toggle).

    • An optional `center` snippet for inline nav (site uses it on
      desktop for Pricing / Download links).

    • The IdentityMenu on the right — the single canonical source of
      "who am I, what section am I in, sign out" for the whole app.

  iOS-aware: the bar reserves `env(safe-area-inset-top)` so it clears the
  status bar / dynamic island without per-shell duplication.
-->
<script lang="ts">
  import type { Snippet } from "svelte";

  import { onMount } from "svelte";

  import { AUTH_EVENT, beginLogin, isSignedIn } from "../auth";
  import { APP_NAME, WORDMARK, markUrl } from "../brand";
  import { AppConfigUnavailableError, resolveAppConfig } from "../config";
  import { go, router, type Section } from "../router.svelte";
  import IdentityMenu from "./IdentityMenu.svelte";
  import { toasts } from "./toast.svelte";

  let {
    leading,
    center,
  }: {
    leading?: Snippet;
    center?: Snippet;
  } = $props();

  let signedIn = $state(false);

  function refresh() {
    signedIn = isSignedIn();
  }

  onMount(() => {
    refresh();
    window.addEventListener(AUTH_EVENT, refresh);
    return () => window.removeEventListener(AUTH_EVENT, refresh);
  });

  // The breadcrumb label appended after "Optio ›". Empty on /site so the
  // marketing surfaces feel like "home" rather than a sub-section.
  const SECTION_LABEL: Record<Section, string> = {
    site: "",
    remote: "Remote",
    admin: "Admin",
  };
  const sectionLabel = $derived(SECTION_LABEL[router.section]);

  async function signIn() {
    try {
      const cfg = await resolveAppConfig();
      await beginLogin(cfg.oidc, router.path);
    } catch (err) {
      // Almost always means we couldn't reach /app-config.json (iOS http
      // capability allowlist miss, dead cloud, wrong baked VITE_API_BASE_URL).
      // The old silent fallback used to redirect to a dead localhost:8000;
      // surface the real reason instead.
      const detail =
        err instanceof AppConfigUnavailableError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Sign-in failed.";
      toasts.error(detail);
      console.warn("[appbar] sign-in failed:", err);
    }
  }
</script>

<header
  class="appbar sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-base/85 px-4 backdrop-blur-md sm:px-6"
  style="padding-top: calc(0.5rem + env(safe-area-inset-top)); padding-bottom: 0.5rem;"
>
  {#if leading}
    {@render leading()}
  {/if}

  <!-- Brand + breadcrumb. The mark replaces the previous accent dot —
       same visual role (small splash of brand colour at the start of the
       bar) but now carries the actual identity. It scales up subtly on
       hover to communicate interactivity. -->
  <a
    href="/"
    onclick={(e) => go(e, "/")}
    class="brand group flex min-w-0 items-center gap-2 text-ink"
    aria-label={`${APP_NAME} home`}
  >
    <img src={markUrl} alt="" class="brand-mark" aria-hidden="true" />
    <span class="brand-wordmark">{WORDMARK}</span>
    {#if sectionLabel}
      <span class="text-ink-faint" aria-hidden="true">›</span>
      <span class="truncate text-sm text-ink-dim">{sectionLabel}</span>
    {/if}
  </a>

  <!-- Spacer — always flex-1, so the identity / Sign-in affordance is
       pinned to the far right at every viewport size. The optional
       `center` snippet is *displayed* inside the spacer only on sm+;
       on mobile its content collapses but the spacer keeps its space.
       (The previous `hidden sm:flex` on the wrapper made the spacer
       itself disappear on mobile, dragging the avatar to the left.) -->
  <div class="flex min-w-0 flex-1 justify-center">
    {#if center}
      <div class="hidden min-w-0 sm:flex">
        {@render center()}
      </div>
    {/if}
  </div>

  <div class="flex items-center gap-2">
    {#if signedIn}
      <IdentityMenu />
    {:else}
      <button
        type="button"
        onclick={signIn}
        class="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white transition hover:bg-accent-hi"
      >
        Sign in
      </button>
    {/if}
  </div>
</header>

<style>
  /* The mark is the persistent identity splash on the bar — 24px square
     so it sits at the height of the wordmark without forcing the bar to
     grow. A subtle scale on hover signals the brand is interactive,
     mirroring the previous accent-dot's pulse without the box-shadow
     glow (which would clash with the mark's hard geometric edges). */
  .brand-mark {
    width: 24px;
    height: 24px;
    display: block;
    flex-shrink: 0;
    /* Optical centering: the comb's bars all sit in the mark's
       upper-right quadrant, so its geometric center reads HIGH against
       the round "o". Nudge it down so the mark and the "o" share a
       centerline. `--mark-dy` is the single knob — bump it if the mark
       still reads high, lower it if it now sits low. */
    --mark-dy: 3px;
    transform: translateY(var(--mark-dy));
    transition: transform 200ms ease;
  }
  /* "optio" — matched to the lockup at /optio-logo.svg. The mark is a
     standalone brand symbol, not a letter; the wordmark is the full
     lowercase word. Fredoka 600 so the wordmark reads on the same
     typographic line as the sibling chaintrust project's "chain" /
     "trust" headers. Font-size is bumped so the 'o' x-height matches
     the mark's visible content height (24px × 320/500 ≈ 15px → x-height
     ≈ 15px at font-size ~28px). */
  .brand-wordmark {
    font-family: "Fredoka", system-ui, sans-serif;
    font-weight: 600;
    font-size: 1.625rem;
    letter-spacing: -0.02em;
    line-height: 1;
  }
  .brand:hover .brand-mark {
    transform: translateY(var(--mark-dy)) scale(1.06);
  }
  @media (prefers-reduced-motion: reduce) {
    .brand-mark {
      transition: none;
    }
  }
</style>
